//@flow
import {useEffect, useState, useCallback} from 'react';
import {
  listChatbotsService,
  createChatbotService,
  editChatbotService,
  getChatbotService,
  getTrainingService,
  trainChatbotService,
  deleteTrainingService,
  deleteChatbotService,
} from './promises';

import {uploadFileService} from '../file-storage/promises';
import type {TrainChatBotServiceRequest} from './promises';

import type {
  Pagination,
  ChatbotTraining,
  ChatbotReplySchedule,
} from '../../entities';

export function useChatBots() {
  const [chatBots, setChatBots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listChatbotsService()
      .then(response => {
        setChatBots(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const refresh = () => {
    setLoading(true);
    listChatbotsService()
      .then(response => {
        setChatBots(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return {chatBots, loading, refresh};
}

type EditChatBotParams = {
  prompt?: string,
  devices?: {
    id: string,
    ravenService: string,
  }[],
  replySchedule?: ChatbotReplySchedule,
};

export function useChatBotsActions() {
  const [loading, setLoading] = useState(false);

  const createChatBot = async (name: string, prompt: string) => {
    try {
      setLoading(true);
      await createChatbotService(name, prompt);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const editChatBot = async (chatbotId: string, params: EditChatBotParams) => {
    try {
      setLoading(true);
      const {prompt, devices, replySchedule} = params;
      await editChatbotService(chatbotId, {
        prompt,
        devices,
        replySchedule,
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleEnableChatBot = async (chatbotId: string, enabled: boolean) => {
    try {
      setLoading(true);
      await editChatbotService(chatbotId, {enabled});
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteChatBot = async (chatbotId: string) => {
    try {
      setLoading(true);
      await deleteChatbotService(chatbotId);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    createChatBot,
    toggleEnableChatBot,
    deleteChatBot,
    editChatBot,
    loading,
  };
}

export function useChatBot(id: string) {
  const [loading, setLoading] = useState(true);
  const [chatBot, setChatBot] = useState(null);

  useEffect(() => {
    setLoading(true);
    getChatbotService(id)
      .then(response => {
        setChatBot(response);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [id]);

  const refetchChatBot = useCallback(() => {
    try {
      setLoading(true);
      getChatbotService(id)
        .then(response => {
          setChatBot(response);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error(error);
    }
  }, [id]);

  return {
    chatBot,
    loading,
    refetchChatBot,
  };
}

export function useChatBotTraining(id: string) {
  const [chatBotTraining, setChatBotTraining] = useState<ChatbotTraining[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTrainingService(id, {
      page: page,
      size: 12,
    })
      .then(response => {
        if (!response) return;

        setPagination(response.pagination || null);
        setChatBotTraining(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [id, page]);

  const refetchChatBotTraining = useCallback(() => {
    try {
      getTrainingService(id, {
        page: 0,
        size: 12,
      })
        .then(response => {
          setChatBotTraining(response.data);
        })
        .catch(() => {});
    } catch (error) {
      console.error(error);
    }
  }, [id]);

  return {
    chatBotTraining,
    pagination,
    page,
    setPage,
    loading,
    refetchChatBotTraining,
  };
}

type TrainBotParams = {
  trainingType:
    | 'STORE_INFORMATION'
    | 'TEXT'
    | 'PDF'
    | 'TRAINED_MESSAGES'
    | 'PRODUCTS',
  text?: string,
  filesIds?: string[],
  files?: File[],
  messages?: string[],
};

export function useTrainBotActions() {
  const [loading, setLoading] = useState(false);

  const trainBot = async (chatbotId: string, data: TrainBotParams) => {
    try {
      setLoading(true);
      const params: TrainChatBotServiceRequest = {
        trainingType: data.trainingType,
        trainingData: {},
      };

      if (data.trainingType === 'TEXT') {
        params.trainingData.text = data.text;
      }

      if (data.trainingType === 'PDF') {
        let fileIds: string[] = data.filesIds || [];

        if (data.files) {
          for (const file of data.files) {
            const [result] = await uploadFileService(file, {
              filePrefix: 'winehouse/chatbot',
              resourceType: 'PDF',
            });

            if (result) {
              fileIds.push(result.id);
            }
          }
        }

        params.trainingData.pdfFiles = fileIds;
      }

      if (data.trainingType === 'TRAINED_MESSAGES') {
        params.trainingData.trainedMessages = data.messages;
      }

      await trainChatbotService(chatbotId, params);
      return {success: true};
    } catch (error) {
      console.error(error);
      throw new Error(
        'Error al procesar el entrenamiento. Por favor, intenta nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteTraining = async (trainingId: string) => {
    try {
      setLoading(true);
      await deleteTrainingService(trainingId);
      return true;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteTraining,
    trainBot,
    loading,
  };
}
